import apiClient from "../../api/apiClient";
import { useAuth } from "../../context/AuthContext";
import useBusinessResource from "../../hooks/useBusinessResource";
import { reportUrl } from "./useLogisticsReports";
import type { FinancialReportKey } from "./report.types";
import type { FinancialData, FinancialFilters } from "./financialReport.types";

export function useFinancialReport(companyId: number | null, warehouseId: number | null, report: FinancialReportKey,
  filters: FinancialFilters, page: number, pageSize: number, allowed: boolean) {
  const { user } = useAuth();
  const scope = { userId: user?.id, companyId, warehouseId, moduleScope: "logistics" };
  const scopeParams = { company_id: companyId, ...(warehouseId ? { warehouse_id: warehouseId } : {}) };
  const needsOptions = report === "customer-receivables" || report === "supplier-payables";
  const config = { ...scope, scopeLevel: "company" as const, active: allowed, enabled: allowed };
  const data = useBusinessResource<FinancialData>({ ...config, resource: "logistics-financial-report",
    parameters: [report, filters, page, pageSize], load: async signal => {
      const response = await apiClient.get(reportUrl(report), { params: { ...scopeParams, ...filters, page, page_size: pageSize },
        headers: { "X-Company-Id": String(companyId) }, signal, timeout: 60000 });
      if (!response.data?.success || response.data.report_key !== report || !Array.isArray(response.data.rows) ||
        Number(response.data.company?.id) !== Number(companyId) || (response.data.warehouse_id ?? null) !== (warehouseId ?? null)) throw new Error("Réponse du rapport indisponible.");
      return response.data;
    } });
  const options = useBusinessResource<{ entities: { value: string; label: string }[] }>({ ...config,
    enabled: allowed && needsOptions, active: allowed && needsOptions,
    resource: "logistics-financial-options", parameters: [report], load: async signal => {
      const response = await apiClient.get(`${reportUrl(report)}/options`, { params: scopeParams,
        headers: { "X-Company-Id": String(companyId) }, signal, timeout: 60000 });
      if (!response.data?.success || !Array.isArray(response.data.entities)) throw new Error("Les choix du rapport sont indisponibles.");
      return response.data;
    } });
  const errorText = (error: any) => error?.response?.data?.message || error?.message;
  return { data: data.data, loading: data.loading, stale: data.status === "cached", error: errorText(data.error),
    options: options.data?.entities || [], optionsError: errorText(options.error),
    reload: () => { void data.refresh({ force: true }).catch(() => {}); if (needsOptions) void options.refresh({ force: true }).catch(() => {}); } };
}
